import React, { useCallback, useEffect, useState } from 'react';

interface DaySchedule { enabled: boolean; start: string; end: string }
interface NonWorkingDay { startDate: string; endDate: string; reason: string }
interface CalendarData { week: DaySchedule[]; nonWorkingDays: NonWorkingDay[]; configured?: boolean }

const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
// Shown Monday first; the array index stays 0 = Sunday to match the API.
const DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const jsonHeaders = { 'Content-Type': 'application/json' };

async function readError(res: Response, fallback: string): Promise<string> {
  const body = await res.json().catch(() => ({}));
  return body?.detail || body?.error || fallback;
}

const sameCalendar = (a: CalendarData, b: CalendarData) =>
  JSON.stringify([a.week, a.nonWorkingDays]) === JSON.stringify([b.week, b.nonWorkingDays]);

const fmtDay = (v: string) => {
  const [y, m, d] = v.split('-');
  return y && m && d ? `${d}/${m}/${y}` : v;
};

const PlanningCalendar: React.FC = () => {
  const [saved, setSaved] = useState<CalendarData | null>(null);
  const [draft, setDraft] = useState<CalendarData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [newDay, setNewDay] = useState<NonWorkingDay>({ startDate: '', endDate: '', reason: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/planning/calendar', { credentials: 'include' });
      if (!res.ok) throw new Error(await readError(res, 'Error al cargar el calendario'));
      const data: CalendarData = await res.json();
      setSaved(data);
      setDraft(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = !!saved && !!draft && !sameCalendar(saved, draft);

  const setDay = (idx: number, patch: Partial<DaySchedule>) => {
    setNotice(null);
    setDraft((d) => d && { ...d, week: d.week.map((w, i) => (i === idx ? { ...w, ...patch } : w)) });
  };

  const addNonWorkingDay = () => {
    if (!newDay.startDate) { setError('Indica la fecha del paro o festivo'); return; }
    const entry = { ...newDay, endDate: newDay.endDate || newDay.startDate };
    if (entry.endDate < entry.startDate) { setError('La fecha final no puede ser anterior a la inicial'); return; }
    setError(null);
    setNotice(null);
    setDraft((d) => d && {
      ...d,
      nonWorkingDays: [...d.nonWorkingDays, entry].sort((a, b) => a.startDate.localeCompare(b.startDate)),
    });
    setNewDay({ startDate: '', endDate: '', reason: '' });
  };

  const removeNonWorkingDay = (idx: number) => {
    setNotice(null);
    setDraft((d) => d && { ...d, nonWorkingDays: d.nonWorkingDays.filter((_, i) => i !== idx) });
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch('/api/planning/calendar', {
        method: 'PUT',
        credentials: 'include',
        headers: jsonHeaders,
        body: JSON.stringify({ week: draft.week, nonWorkingDays: draft.nonWorkingDays }),
      });
      if (!res.ok) throw new Error(await readError(res, 'Error al guardar el calendario'));
      const data: CalendarData = await res.json();
      setSaved(data);
      setDraft(data);
      setNotice('Calendario guardado. Se aplicará a las próximas simulaciones.');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-5">
      <div>
        <h2 className="text-lg font-bold text-gray-800">🗓️ Calendario de planta</h2>
        <p className="text-sm text-gray-500 mt-1">
          Define los horarios de producción y los días de paro o festivos. Las simulaciones de planeación programan inicio y fin
          solo dentro de este calendario, a partir de la fecha y hora actual (hora de México).
        </p>
        {saved && !saved.configured && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
            Aún no hay un calendario guardado: se asume producción continua (24 horas, 7 días).
          </p>
        )}
      </div>

      {loading && <p className="text-sm text-gray-500">Cargando...</p>}

      {draft && (
        <>
          <div>
            <h3 className="font-semibold text-gray-700 text-sm mb-2">Horario semanal</h3>
            <div className="border border-gray-200 rounded-xl overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-left">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Día</th>
                    <th className="px-3 py-2 font-semibold">Produce</th>
                    <th className="px-3 py-2 font-semibold">Inicio</th>
                    <th className="px-3 py-2 font-semibold">Fin</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {DISPLAY_ORDER.map((idx) => {
                    const d = draft.week[idx];
                    return (
                      <tr key={idx}>
                        <td className="px-3 py-2 font-medium">{DAY_NAMES[idx]}</td>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={d.enabled}
                            disabled={saving}
                            aria-label={`${DAY_NAMES[idx]} produce`}
                            onChange={(e) => setDay(idx, { enabled: e.target.checked })}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="time"
                            value={d.start}
                            disabled={saving || !d.enabled}
                            aria-label={`${DAY_NAMES[idx]} inicio`}
                            onChange={(e) => e.target.value && setDay(idx, { start: e.target.value })}
                            className="border border-gray-300 rounded px-2 py-1 disabled:bg-gray-100"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <input
                              type="time"
                              value={d.end === '24:00' ? '' : d.end}
                              disabled={saving || !d.enabled || d.end === '24:00'}
                              aria-label={`${DAY_NAMES[idx]} fin`}
                              onChange={(e) => e.target.value && setDay(idx, { end: e.target.value })}
                              className="border border-gray-300 rounded px-2 py-1 disabled:bg-gray-100"
                            />
                            <label className="flex items-center gap-1 text-xs text-gray-600">
                              <input
                                type="checkbox"
                                checked={d.end === '24:00'}
                                disabled={saving || !d.enabled}
                                onChange={(e) => setDay(idx, { end: e.target.checked ? '24:00' : '23:00' })}
                              />
                              hasta medianoche
                            </label>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Para un solo turno corrido pon, por ejemplo, 07:00 a 19:00. Para producción continua: 00:00 hasta medianoche.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-700 text-sm mb-1">Días de paro programado y festivos</h3>
            <p className="text-xs text-gray-500 mb-2">En estos días (o rangos) no hay producción, aunque el día de la semana esté activo.</p>
            <div className="flex flex-wrap items-end gap-2 mb-3">
              <label className="text-xs text-gray-600">Desde
                <input type="date" value={newDay.startDate} disabled={saving}
                  onChange={(e) => setNewDay({ ...newDay, startDate: e.target.value })}
                  className="block border border-gray-300 rounded px-2 py-1 text-sm" />
              </label>
              <label className="text-xs text-gray-600">Hasta (opcional)
                <input type="date" value={newDay.endDate} min={newDay.startDate || undefined} disabled={saving}
                  onChange={(e) => setNewDay({ ...newDay, endDate: e.target.value })}
                  className="block border border-gray-300 rounded px-2 py-1 text-sm" />
              </label>
              <label className="text-xs text-gray-600 flex-1 min-w-[180px]">Motivo
                <input value={newDay.reason} maxLength={200} disabled={saving} placeholder="Ej. Mantenimiento, Navidad"
                  onChange={(e) => setNewDay({ ...newDay, reason: e.target.value })}
                  className="block w-full border border-gray-300 rounded px-2 py-1 text-sm" />
              </label>
              <button disabled={saving} onClick={addNonWorkingDay}
                className="px-3 py-1 bg-gray-100 rounded-lg text-sm disabled:opacity-50">Agregar</button>
            </div>
            {draft.nonWorkingDays.length === 0 ? (
              <p className="text-sm text-gray-500">No hay días de paro registrados.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {draft.nonWorkingDays.map((n, i) => (
                  <li key={`${n.startDate}-${n.endDate}-${i}`} className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <span>
                      <span className="font-medium">
                        {fmtDay(n.startDate)}{n.endDate !== n.startDate ? ` al ${fmtDay(n.endDate)}` : ''}
                      </span>
                      {n.reason && <span className="text-gray-600"> — {n.reason}</span>}
                    </span>
                    <button disabled={saving} onClick={() => removeNonWorkingDay(i)}
                      className="text-red-600 text-xs underline disabled:opacity-50">Quitar</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button disabled={!dirty || saving} onClick={save}
              className="px-3 py-1 bg-purple-600 text-white rounded-lg text-sm disabled:opacity-50">
              {saving ? 'Guardando...' : 'Guardar calendario'}
            </button>
            {dirty && !saving && <span className="text-xs text-amber-700">Cambios sin guardar</span>}
          </div>
        </>
      )}

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>}
      {notice && <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-800 text-sm">{notice}</div>}
    </div>
  );
};

export default PlanningCalendar;
